'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { Send, NewChat, Cross, AssistantInfo, TelemarkMark } from '@icons';
import { getDestinationContext } from '@commons/DestinationFilter/destinationContextStore';
import { useDesitnationCollection } from '@commons/DesitnationCollection/useDesitnationCollection';
import ChatbotMessage from './ChatbotMessage';
import useFollowOutput from './useFollowOutput';
import useOverlayViewport from './useOverlayViewport';
import { buildPostIndex, lookupPostByHref, buildDeadLinkCheck } from './chatbotMentions';

/** Stable id for one conversation, so each logged turn upserts the same WP row. */
function newConversationId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The assistant lives as a persistent, full-width input bar pinned to the bottom
 * of every page. Focusing the input (or sending a message) opens a full-page
 * overlay that renders the conversation with plenty of width for rich answers.
 * Closing the overlay keeps the bar — and the conversation — alive.
 *
 * @param {{ lang?: string, slug?: string, greeting?: string, disclaimer?: string, title?: string, placeholder?: string, dictionary?: Record<string, string> }} props
 */
export default function Chatbot({
  lang = 'no',
  slug = 'default',
  greeting = '',
  disclaimer = '',
  title = '',
  placeholder = '',
  dictionary = {}
}) {
  // The assistant may override its heading and input placeholder in WP admin;
  // fall back to the language dictionary when those fields are left empty.
  const chatTitle = title || dictionary.title;
  const chatPlaceholder = placeholder || dictionary.placeholder;
  // Read the visitor's current front-page interest filters at send time and
  // attach them to each request. When prepareSendMessagesRequest returns a body
  // it replaces the default entirely, so messages/trigger must be passed through.
  // Stable across re-renders and read at send time, so the memoized transport
  // always sees the current id; reset (with the messages) on "new chat".
  const conversationIdRef = useRef('');
  if (!conversationIdRef.current) conversationIdRef.current = newConversationId();

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: '/api/chat',
        body: { lang },
        prepareSendMessagesRequest: ({ id, messages, trigger, messageId, body }) => ({
          body: {
            ...body,
            id,
            messages,
            trigger,
            messageId,
            assistantSlug: slug,
            destination: getDestinationContext(),
            conversationId: conversationIdRef.current
          }
        })
      }),
    [lang, slug]
  );

  // throttle: coalesce streamed UI updates to ~one per 50ms so React doesn't
  // re-render (and re-scroll) on every token — much smoother text + scroll.
  const { messages, sendMessage, setMessages, status, error } = useChat({ transport, experimental_throttle: 50 });
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  // The conversation scroller keeps following the answer as it is written, and
  // yields the moment the visitor scrolls up to re-read something (see
  // useFollowOutput).
  const messagesRef = useRef(null);
  const messagesInnerRef = useRef(null);
  const inputRef = useRef(null);
  const { scrollToBottom, resume: resumeFollow } = useFollowOutput({
    scrollerRef: messagesRef,
    contentRef: messagesInnerRef,
    active: open
  });

  // The posts loaded in the browser, matched against the pages the assistant
  // links to in its answers (see chatbotMentions.js).
  const { products, events, pages } = useDesitnationCollection();
  const collection = useMemo(
    () => [...(products ?? []), ...(events ?? []), ...(pages ?? [])],
    [products, events, pages]
  );
  const postIndex = useMemo(() => buildPostIndex(collection), [collection]);
  // Resolve an inline link's href to a full post; each answer renders the posts
  // it links to as a horizontal blurb list beneath its text (see ChatbotMessage).
  const lookup = useCallback(/** @param {string} href */ (href) => lookupPostByHref(postIndex, href), [postIndex]);
  // Answers occasionally carry a url the model rewrote rather than copied; those
  // render as plain text instead of sending the visitor to a 404 (see
  // buildDeadLinkCheck). Memoised so the markdown renderer stays stable.
  const isDeadLink = useMemo(() => buildDeadLinkCheck(postIndex), [postIndex]);

  // The id of the first answer in the thread — it gets the avatar + name header.
  const firstAssistantId = useMemo(() => messages.find((message) => message.role === 'assistant')?.id, [messages]);

  // Close the overlay on navigation (e.g. clicking a link in an answer).
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const isBusy = status === 'submitted' || status === 'streaming';
  const isEmpty = messages.length === 0;

  // Lock background scroll while the overlay is open, and close it on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('Chatbot-locked');

    // `overflow: hidden` is not enough on iOS. When the field takes focus,
    // Safari scrolls the *document* to bring it above the keyboard — it may do
    // that even to a document whose overflow is hidden — and that drags the
    // fixed overlay off the visual viewport, leaving the visitor looking at,
    // and flicking, the page behind it. Pinning body takes the document scroll
    // away entirely, so there is nothing left for Safari to scroll.
    //
    // Only where a keyboard can do that, though: pinning body also stops
    // Firefox from rendering backdrop-filter on the overlay, so the frost
    // turns into a flat white sheet. Verified in Firefox 148 — the frost
    // survives the root's overflow lock and disappears the moment body is
    // pinned. A pointer-driven browser has no on-screen keyboard to shove the
    // viewport around, so it keeps the frost and makes do with the root lock.
    const pinDocument = window.matchMedia?.('(pointer: coarse)').matches ?? false;
    const { scrollY } = window;
    const lockedPath = window.location.pathname;
    const { style } = document.body;
    if (pinDocument) {
      style.position = 'fixed';
      style.top = `-${scrollY}px`;
      style.left = '0';
      style.right = '0';
      style.width = '100%';
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('Chatbot-locked');
      if (!pinDocument) return;
      style.position = '';
      style.top = '';
      style.left = '';
      style.right = '';
      style.width = '';
      // Pinning body scrolled the page to the top; put the visitor back — but
      // not when the overlay closed because a link in an answer navigated us
      // away, where the new page belongs at its own top. `instant` because the
      // root sets scroll-behavior: smooth, which would animate the restore.
      if (window.location.pathname === lockedPath) {
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      }
    };
  }, [open]);

  // Zoom lock and keyboard handling, including keeping the latest text in view
  // as the keyboard opens and closes (see useOverlayViewport).
  useOverlayViewport(open, scrollToBottom);

  // Streamed updates are throttled to ~20/s (see useChat); this covers browsers
  // without ResizeObserver, and costs nothing where the observer already fired
  // (the hook coalesces both into one scroll per frame).
  useEffect(() => {
    scrollToBottom();
  }, [messages, status, scrollToBottom]);

  const submit = (event) => {
    event.preventDefault();
    const text = input.trim();
    if (!text || isBusy) return;
    // A new question always jumps back to the live end of the conversation.
    resumeFollow();
    sendMessage({ text });
    setInput('');
    setOpen(true);
    // On a phone the keyboard covers most of the answer, and sending with the
    // return key would otherwise leave the visitor reading through a slit until
    // they dismiss it themselves. Sending is the point where they are done
    // typing, so let the field go — pointer:coarse keeps desktop focus intact.
    if (window.matchMedia?.('(pointer: coarse)').matches) inputRef.current?.blur();
  };

  // Reset the conversation: clear the messages and start a fresh logged thread.
  const startNewChat = () => {
    setMessages([]);
    conversationIdRef.current = newConversationId();
  };

  return (
    <div className={`Chatbot ${open ? 'is-open' : ''}`}>
      {open && (
        <div
          className="Chatbot__overlay Chatbot__overlay--light"
          role="dialog"
          aria-modal="true"
          aria-label={chatTitle || 'Chat'}
        >
          <button
            type="button"
            className="Chatbot__backdrop"
            aria-label={dictionary.close || 'Close'}
            onClick={() => setOpen(false)}
          />

          <div className="Chatbot__panel">
            <button
              type="button"
              className="Chatbot__close"
              onClick={() => setOpen(false)}
              aria-label={dictionary.close || 'Close'}
              title={dictionary.close || 'Close'}
            >
              <Cross />
            </button>

            <div className="Chatbot__body">
              <div className={`Chatbot__messages ${isEmpty ? 'is-empty' : ''}`} ref={messagesRef}>
                <div className="Chatbot__messages-inner" ref={messagesInnerRef}>
                  {isEmpty && (greeting || chatTitle) && (
                    <div className="Chatbot__message Chatbot__message--assistant Chatbot__greeting">
                      <div className="Chatbot__text">
                        <div className="Chatbot__identity">
                          <TelemarkMark className="Chatbot__avatar" aria-hidden="true" />
                          {chatTitle && <span className="Chatbot__assistant-name">{chatTitle}</span>}
                        </div>
                        {greeting && <p>{greeting}</p>}
                      </div>
                    </div>
                  )}

                  {messages.map((message, index) => (
                    <ChatbotMessage
                      key={message.id}
                      message={message}
                      dictionary={dictionary}
                      lookup={lookup}
                      isDeadLink={isDeadLink}
                      assistantName={chatTitle}
                      showIdentity={message.id === firstAssistantId}
                      /* Only the answer currently being written is half-finished
                         markdown (see streamingMarkdown.js). */
                      isStreaming={status === 'streaming' && index === messages.length - 1}
                    />
                  ))}

                  {status === 'submitted' && (
                    <div className="Chatbot__typing" aria-live="polite">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  )}

                  {error && (
                    <div className="Chatbot__error" role="alert">
                      {dictionary.errorText || 'Something went wrong. Please try again.'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {disclaimer && (
              <div className="Chatbot__disclaimer">
                <AssistantInfo aria-hidden="true" />
                <span>{disclaimer}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <form className="Chatbot__bar" onSubmit={submit} autoComplete="off">
        <div className="Chatbot__bar-inner">
          <div className="Chatbot__field">
            {/* No autofill in the prompt: browsers ignore autocomplete="off" on a plain
                text field unless the name is unrecognised, and the password managers
                need their own opt-outs (data-lpignore / data-1p-ignore). */}
            <input
              ref={inputRef}
              type="text"
              className="Chatbot__input"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onFocus={() => setOpen(true)}
              placeholder={chatPlaceholder || ''}
              aria-label={chatPlaceholder || chatTitle}
              name="chatbot-prompt"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="sentences"
              spellCheck={false}
              enterKeyHint="send"
              data-lpignore="true"
              data-1p-ignore
              data-form-type="other"
            />
            <button
              type="submit"
              className="Chatbot__send"
              disabled={isBusy || !input.trim()}
              aria-label={dictionary.send || 'Send'}
            >
              <Send />
            </button>
          </div>
        </div>

        {false && !isEmpty && (
          <div className="Chatbot__bar-actions">
            <button type="button" className="Chatbot__new-chat" onClick={startNewChat}>
              <NewChat />
              <span>{dictionary.newChat || 'Ny chatt'}</span>
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
